import React from "react";
import { endOfDay, startOfDay } from "date-fns";
import { DateRange } from "react-day-picker";
import { useSearchParams } from "react-router-dom";
import {
  DEFAULT_LOGO_CLASS_NAME,
  GuesteriaPartnerRevenueRow,
  mapOverviewToRow,
} from "./guesteriaPartnerRevenueModel";
import { GuesteriaPartnersRevenueFilters } from "./components/GuesteriaPartnersRevenueFilters";
import { GuesteriaPartnersRevenueStats } from "./components/GuesteriaPartnersRevenueStats";
import { GuesteriaPartnersRevenueTable } from "./components/GuesteriaPartnersRevenueTable";
import { partnerDashboardService } from "@/services/partner-dashboard-service";
import {
  PartnerExportFormat,
  PartnerHotelFilterOption,
  PartnerHotelRevenueRank,
  PartnerTotalRevenueSummary,
} from "@/types/partner-dashboard";
import { LocalStorageKeys } from "../login/hooks/useLocalStorage";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";

const PAGE_SIZE = 10;

function getCurrentMonthRange(): DateRange {
  const now = new Date();
  return {
    from: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: endOfDay(now),
  };
}

function toIsoDateRange(range?: DateRange): { from: string; to: string } {
  const fallback = getCurrentMonthRange();
  const fromDate = startOfDay(range?.from ?? fallback.from!);
  const toDate = endOfDay(range?.to ?? range?.from ?? fallback.to!);

  return {
    from: fromDate.toISOString(),
    to: toDate.toISOString(),
  };
}

function mapFilterOptionToPopoverOption(option: PartnerHotelFilterOption): {
  id: string;
  name: string;
  logoId?: string | null;
  logoClassName: string;
  logoLetter: string;
} {
  return {
    id: option.id,
    name: option.name,
    logoId: option.logoId,
    logoClassName: DEFAULT_LOGO_CLASS_NAME,
    logoLetter: option.name.charAt(0).toUpperCase(),
  };
}

function mapUiExportFormat(format: "CSV" | "Excel"): PartnerExportFormat {
  return format === "CSV" ? "csv" : "xlsx";
}

function compareRows(
  a: GuesteriaPartnerRevenueRow,
  b: GuesteriaPartnerRevenueRow,
  field: string,
  direction: "asc" | "desc",
): number {
  const dir = direction === "asc" ? 1 : -1;
  const av = a[field as keyof GuesteriaPartnerRevenueRow];
  const bv = b[field as keyof GuesteriaPartnerRevenueRow];
  if (typeof av === "number" && typeof bv === "number") {
    return (av - bv) * dir;
  }
  const as = String(av ?? "");
  const bs = String(bv ?? "");
  return as.localeCompare(bs) * dir;
}

export const GuesteriaPartnersRevenuePage = (): React.JSX.Element => {
  const { capabilities } = useAccessCapabilities();
  const canExport = capabilities.canManageBilling;
  const [searchParams] = useSearchParams();
  const tenantIdFromUrl = searchParams.get("tenantId") || undefined;
  const tenantId =
    tenantIdFromUrl ??
    (typeof window !== "undefined"
      ? localStorage.getItem(LocalStorageKeys.TENANT_ID) || undefined
      : undefined);

  const [sortField, setSortField] = React.useState<string | null>(null);
  const [sortDirection, setSortDirection] = React.useState<
    "asc" | "desc" | null
  >(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [selectedHotels, setSelectedHotels] = React.useState<string[]>([]);
  const [selectedExportFormat, setSelectedExportFormat] = React.useState<
    "CSV" | "Excel" | null
  >("CSV");
  const [dateRange, setDateRange] = React.useState<DateRange | undefined>(
    getCurrentMonthRange(),
  );
  const [rows, setRows] = React.useState<GuesteriaPartnerRevenueRow[]>([]);
  const [hotelOptions, setHotelOptions] = React.useState<
    Array<{
      id: string;
      name: string;
      logoId?: string | null;
      logoClassName: string;
      logoLetter: string;
    }>
  >([]);
  const [loading, setLoading] = React.useState(false);
  const [topRevenueHotel, setTopRevenueHotel] =
    React.useState<PartnerHotelRevenueRank | null>(null);
  const [lowestRevenueHotel, setLowestRevenueHotel] =
    React.useState<PartnerHotelRevenueRank | null>(null);
  const [totalRevenueSummary, setTotalRevenueSummary] =
    React.useState<PartnerTotalRevenueSummary | null>(null);

  React.useEffect(() => {
    if (!tenantId) {
      setHotelOptions([]);
      return;
    }

    let isMounted = true;
    const loadHotelFilterOptions = async () => {
      try {
        const data =
          await partnerDashboardService.getHotelFilterOptions(tenantId);
        if (isMounted) {
          setHotelOptions(data.map(mapFilterOptionToPopoverOption));
        }
      } catch {
        if (isMounted) {
          setHotelOptions([]);
        }
      }
    };

    void loadHotelFilterOptions();
    return () => {
      isMounted = false;
    };
  }, [tenantId]);

  React.useEffect(() => {
    if (!tenantId) {
      setRows([]);
      return;
    }

    const controller = new AbortController();
    const loadHotelOverview = async () => {
      try {
        setLoading(true);
        const { from, to } = toIsoDateRange(dateRange);
        const data = await partnerDashboardService.getHotels(tenantId, {
          from,
          to,
          hotelIds: selectedHotels.length > 0 ? selectedHotels : undefined,
        });

        if (!controller.signal.aborted) {
          setRows(data.map(mapOverviewToRow));
        }
      } catch {
        if (!controller.signal.aborted) {
          setRows([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    void loadHotelOverview();

    return () => controller.abort();
  }, [dateRange, selectedHotels, tenantId]);

  React.useEffect(() => {
    if (!tenantId) {
      setTopRevenueHotel(null);
      setLowestRevenueHotel(null);
      setTotalRevenueSummary(null);
      return;
    }

    let isMounted = true;
    const loadStats = async () => {
      try {
        const { from, to } = toIsoDateRange(dateRange);
        const [top, lowest, total] = await Promise.all([
          partnerDashboardService.getTopRevenueHotel(tenantId, { from, to }),
          partnerDashboardService.getLowestRevenueHotel(tenantId, { from, to }),
          partnerDashboardService.getTotalRevenue(tenantId, { from, to }),
        ]);

        if (isMounted) {
          setTopRevenueHotel(top);
          setLowestRevenueHotel(lowest);
          setTotalRevenueSummary(total);
        }
      } catch {
        if (isMounted) {
          setTopRevenueHotel(null);
          setLowestRevenueHotel(null);
          setTotalRevenueSummary(null);
        }
      }
    };

    void loadStats();
    return () => {
      isMounted = false;
    };
  }, [dateRange, tenantId]);

  const filteredRows = React.useMemo(() => {
    return rows;
  }, [rows]);

  const sortedRows = React.useMemo(() => {
    const rows = [...filteredRows];
    if (!sortField || !sortDirection) {
      return rows;
    }
    return rows.sort((a, b) => compareRows(a, b, sortField, sortDirection));
  }, [filteredRows, sortField, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));

  const paginatedRows = React.useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedRows.slice(start, start + PAGE_SIZE);
  }, [sortedRows, currentPage]);

  React.useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedHotels]);

  const handleSort = (field: string) => {
    setCurrentPage(1);
    if (sortField !== field) {
      setSortField(field);
      setSortDirection("asc");
    } else if (sortDirection === "asc") {
      setSortDirection("desc");
    } else {
      setSortField(null);
      setSortDirection(null);
    }
  };

  const handleExport = React.useCallback(
    async (format: "CSV" | "Excel" | null) => {
      if (!canExport) return;

      setSelectedExportFormat(format);
      if (!format) return;

      if (!tenantId) return;

      const { from, to } = toIsoDateRange(dateRange);
      const exportFormat = mapUiExportFormat(format);
      const blob = await partnerDashboardService.exportHotels(tenantId, {
        from,
        to,
        hotelIds: selectedHotels.length > 0 ? selectedHotels : undefined,
        format: exportFormat,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `hotel-partner-revenue.${exportFormat}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    [canExport, dateRange, selectedHotels, tenantId],
  );

  return (
    <div className="space-y-6 p-6">
      <div className="space-y-3">
        <GuesteriaPartnersRevenueStats
          topRevenueHotel={topRevenueHotel}
          lowestRevenueHotel={lowestRevenueHotel}
          totalRevenueSummary={totalRevenueSummary}
        />
        <GuesteriaPartnersRevenueFilters
          hotelOptions={hotelOptions}
          selectedHotels={selectedHotels}
          dateRange={dateRange}
          selectedExportFormat={selectedExportFormat}
          onHotelsApply={setSelectedHotels}
          onDateApply={setDateRange}
          onExportApply={(format) => {
            void handleExport(format);
          }}
          canExport={canExport}
        />
      </div>

      <GuesteriaPartnersRevenueTable
        data={paginatedRows}
        loading={loading}
        currentPage={currentPage}
        totalPages={totalPages}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};
